// Register.jsx - Complete corrected version
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  RiUserLine,
  RiMailLine,
  RiPhoneLine,
  RiBuildingLine,
  RiBriefcaseLine,
  RiArrowRightLine,
  RiCheckLine,
  RiTicketLine,
  RiHotelLine,
  RiHome2Line,
  RiAddLine,
  RiSubtractLine,
  RiCalendarEventLine,
  RiUserAddLine,
} from "react-icons/ri";
import toast, { Toaster } from "react-hot-toast";
import axios from "axios";
import { API_BASE } from "../config";

axios.defaults.baseURL = API_BASE;

/* ─── FONT IMPORT ────────────────────────────────────────────────────────────*/
const FontImport = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Inter:wght@300;400;500;600;700;800;900&display=swap');
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    html { scroll-behavior: smooth; }
    body { background: #F4F9F8; }
    ::-webkit-scrollbar { width: 8px; }
    ::-webkit-scrollbar-track { background: #E8F5F3; }
    ::-webkit-scrollbar-thumb { background: #2A9D8F; border-radius: 4px; }
    @keyframes spin { to { transform: rotate(360deg); } }
  `}</style>
);

/* ─── THEME COLORS ────────────────────────────────────────────────────────────*/
const T = {
  white: "#FFFFFF",
  offWhite: "#F4F9F8",
  softTeal: "#E8F5F3",
  primary: "#2A9D8F",
  primaryDark: "#1F7A6E",
  primaryLight: "#3BBCAE",
  navy: "#1A3A4A",
  navyDeep: "#0D2B3E",
  accent: "#3BBCAE",
  accentGold: "#D4A017",
  accentGoldLight: "#E8B520",
  accentTealLight: "#6BC2B5",
  accentTealDark: "#218577",
  text: "#1C3461",
  textMid: "#2D4A5A",
  textMuted: "#6B8090",
  textLight: "#9BB0C0",
  border: "#C8E8E4",
  borderLight: "#E2F2F0",
  shadow: "0 4px 24px rgba(42,157,143,0.10)",
  shadowMd: "0 8px 40px rgba(42,157,143,0.14)",
  shadowLg: "0 20px 60px rgba(13,43,62,0.20)",
  shadowGold: "0 8px 32px rgba(212,160,23,0.25)",
  gradPrimary: "linear-gradient(135deg, #2A9D8F 0%, #3BBCAE 100%)",
  gradNavy: "linear-gradient(135deg, #0D2B3E 0%, #1A3A4A 100%)",
  gradSoft: "linear-gradient(135deg, #E8F5F3 0%, #F4F9F8 100%)",
  gradGold: "linear-gradient(135deg, #D4A017 0%, #E8B520 100%)",
  success: "#10B981",
};

/* ─── STATIC DATA ────────────────────────────────────────────────────────────*/
const steps = [
  { number: 1, title: "Personal Info", icon: RiUserLine },
  { number: 2, title: "Select Pass", icon: RiTicketLine },
  { number: 3, title: "Accommodation", icon: RiHotelLine },
  { number: 4, title: "Confirm", icon: RiCheckLine },
  { number: 5, title: "Complete", icon: RiCheckLine },
];

const defaultAccommodationOption = {
  value: "none",
  label: "No accommodation needed",
  price: 0,
  icon: RiHome2Line,
  description: "Arrange your own accommodation",
  features: [],
};

/* ─── HELPERS ────────────────────────────────────────────────────────────────*/
const inputStyle = (focused) => ({
  width: "100%",
  padding: "12px 16px",
  background: T.white,
  border: `1px solid ${focused ? T.primary : T.border}`,
  borderRadius: 12,
  fontSize: 14,
  color: T.text,
  outline: "none",
  transition: "all 0.2s",
  fontFamily: "'Inter', sans-serif",
});

const iconInputStyle = (focused) => ({
  ...inputStyle(focused),
  paddingLeft: 42,
});

function SectionLabel({ label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
      <span style={{
        fontSize: 11, fontWeight: 700, color: T.primary, letterSpacing: "0.12em",
        textTransform: "uppercase", whiteSpace: "nowrap"
      }}>{label}</span>
      <div style={{ flex: 1, height: 1, background: T.borderLight }} />
    </div>
  );
}

function StepIndicator({ currentStep }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "center",
      marginBottom: 48, flexWrap: "wrap", gap: 4,
    }}>
      {steps.map((step, index) => (
        <React.Fragment key={step.number}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{
              width: 40, height: 40, borderRadius: "50%", display: "flex",
              alignItems: "center", justifyContent: "center", fontFamily: "'Inter', sans-serif",
              fontWeight: 700, fontSize: 14, transition: "all 0.3s",
              background: currentStep >= step.number ? T.gradPrimary : T.white,
              color: currentStep >= step.number ? T.white : T.textMuted,
              border: `1px solid ${currentStep >= step.number ? "transparent" : T.border}`,
              boxShadow: currentStep >= step.number ? T.shadow : "none",
            }}>
              {currentStep > step.number ? <RiCheckLine size={18} /> : step.number}
            </div>
          </div>
          {index < steps.length - 1 && (
            <div style={{
              width: 40, height: 2,
              background: currentStep > step.number ? T.primary : T.border,
              transition: "background 0.3s", margin: "0 4px",
            }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function PassCard({ pass, isSelected, onSelect, index }) {
  const [hovered, setHovered] = useState(false);
  const accent = pass.color || T.primary;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onSelect(pass.value)}
      style={{
        background: isSelected ? `${accent}10` : T.white,
        borderRadius: 20,
        border: isSelected ? `2px solid ${accent}` : `1px solid ${T.border}`,
        cursor: "pointer",
        transition: "all 0.25s",
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
        position: "relative",
        overflow: "hidden",
        padding: "28px 24px",
        boxShadow: T.shadow,
      }}
    >
      {isSelected && (
        <div style={{
          position: "absolute", top: 14, right: 14, width: 24, height: 24,
          borderRadius: "50%", background: accent, display: "flex",
          alignItems: "center", justifyContent: "center",
        }}>
          <RiCheckLine size={14} color={T.white} />
        </div>
      )}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 3,
        background: accent, opacity: isSelected ? 1 : 0, transition: "opacity 0.25s",
      }} />

      <div style={{
        fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 800,
        color: T.text, marginBottom: 6,
      }}>{pass.label}</div>
      <div style={{
        fontFamily: "'Playfair Display', serif", fontSize: 32, fontWeight: 900,
        color: accent, marginBottom: 20,
      }}>{pass.priceDisplay || `$${pass.price}`}</div>

      <div style={{ height: 1, background: T.border, marginBottom: 20 }} />

      <ul style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {pass.features.slice(0, 4).map((feature, i) => (
          <li key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: T.textMuted }}>
            <RiCheckLine size={13} style={{ color: accent, flexShrink: 0 }} />
            {feature}
          </li>
        ))}
        {pass.features.length > 4 && (
          <li style={{ fontSize: 12, color: T.textMuted, fontStyle: "italic" }}>
            +{pass.features.length - 4} more benefits
          </li>
        )}
      </ul>
    </motion.div>
  );
}

function AccommodationCard({ accommodation, isSelected, onSelect, index }) {
  const [hovered, setHovered] = useState(false);
  const Icon = accommodation.icon || RiHotelLine;
  const accent = accommodation.color || T.primary;

  // Handle "none" option differently
  if (accommodation.value === "none") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: index * 0.08 }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => onSelect(accommodation.value)}
        style={{
          background: isSelected ? `${T.primary}10` : T.white,
          borderRadius: 16,
          border: isSelected ? `2px solid ${T.primary}` : `1px solid ${T.border}`,
          cursor: "pointer",
          transition: "all 0.25s",
          transform: hovered ? "translateY(-3px)" : "translateY(0)",
          position: "relative",
          padding: "22px",
          boxShadow: T.shadow,
          minHeight: "200px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center"
        }}
      >
        <div style={{
          width: 46, height: 46, borderRadius: 11, background: `${T.primary}10`,
          border: `1px solid ${T.primary}20`, display: "flex",
          alignItems: "center", justifyContent: "center", marginBottom: 14,
        }}>
          <RiHome2Line size={22} color={T.primary} />
        </div>
        <div style={{
          fontFamily: "'Playfair Display', serif", fontSize: 17, fontWeight: 800,
          color: T.text, marginBottom: 6,
        }}>{accommodation.label}</div>
        <p style={{ fontSize: 13, color: T.textMuted, marginBottom: 14, lineHeight: 1.6 }}>
          {accommodation.description}
        </p>
        {isSelected && (
          <div style={{
            position: "absolute", top: 14, right: 14, width: 22, height: 22,
            borderRadius: "50%", background: T.primary, display: "flex",
            alignItems: "center", justifyContent: "center",
          }}>
            <RiCheckLine size={13} color={T.white} />
          </div>
        )}
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onSelect(accommodation.value)}
      style={{
        background: isSelected ? `${accent}10` : T.white,
        borderRadius: 16,
        border: isSelected ? `2px solid ${accent}` : `1px solid ${T.border}`,
        cursor: "pointer",
        transition: "all 0.25s",
        transform: hovered ? "translateY(-3px)" : "translateY(0)",
        position: "relative",
        padding: "22px",
        boxShadow: T.shadow,
        minHeight: "180px",
      }}
    >
      {isSelected && (
        <div style={{
          position: "absolute", top: 14, right: 14, width: 22, height: 22,
          borderRadius: "50%", background: accent, display: "flex",
          alignItems: "center", justifyContent: "center",
        }}>
          <RiCheckLine size={13} color={T.white} />
        </div>
      )}

      <div style={{
        width: 46, height: 46, borderRadius: 11, background: `${accent}10`,
        border: `1px solid ${accent}20`, display: "flex",
        alignItems: "center", justifyContent: "center", marginBottom: 14,
      }}>
        <Icon size={22} color={accent} />
      </div>

      <div style={{
        fontFamily: "'Playfair Display', serif", fontSize: 17, fontWeight: 800,
        color: T.text, marginBottom: 6,
      }}>{accommodation.label}</div>

      {accommodation.price > 0 && (
        <div style={{ fontSize: 22, fontWeight: 800, color: accent, marginBottom: 10 }}>
          ${accommodation.price}
          <span style={{ fontSize: 12, fontWeight: 400, color: T.textMuted }}> / night</span>
        </div>
      )}

      {accommodation.description && (
        <p style={{ fontSize: 13, color: T.textMuted, marginBottom: 14, lineHeight: 1.6 }}>
          {accommodation.description}
        </p>
      )}
      
      {accommodation.features && accommodation.features.length > 0 && (
        <div style={{ marginTop: 8, textAlign: "left" }}>
          {accommodation.features.slice(0, 3).map((feature, idx) => (
            <div key={idx} style={{ fontSize: 12, color: T.textMuted, padding: "2px 0", display: "flex", alignItems: "center", gap: "4px" }}>
              <span style={{ color: accent }}>✓</span> {feature}
            </div>
          ))}
          {accommodation.features.length > 3 && (
            <div style={{ fontSize: 11, color: T.textMuted, fontStyle: "italic", marginTop: 4 }}>
              +{accommodation.features.length - 3} more benefits
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}

function NightCounter({ nights, onNightsChange, minNights = 1, maxNights = 7 }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
      <button
        type="button"
        onClick={() => nights > minNights && onNightsChange(nights - 1)}
        disabled={nights <= minNights}
        style={{
          width: 40, height: 40, borderRadius: 10, background: nights <= minNights ? T.offWhite : T.primary,
          border: `1px solid ${nights <= minNights ? T.border : T.primary}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: nights <= minNights ? "not-allowed" : "pointer", opacity: nights <= minNights ? 0.5 : 1,
          transition: "all 0.2s",
        }}
      >
        <RiSubtractLine size={16} color={nights <= minNights ? T.textMuted : T.white} />
      </button>
      <div style={{ minWidth: 80, textAlign: "center", fontSize: 18, fontWeight: 700, color: T.text }}>
        {nights} {nights === 1 ? "Night" : "Nights"}
      </div>
      <button
        type="button"
        onClick={() => nights < maxNights && onNightsChange(nights + 1)}
        disabled={nights >= maxNights}
        style={{
          width: 40, height: 40, borderRadius: 10, background: nights >= maxNights ? T.offWhite : T.primary,
          border: `1px solid ${nights >= maxNights ? T.border : T.primary}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: nights >= maxNights ? "not-allowed" : "pointer", opacity: nights >= maxNights ? 0.5 : 1,
          transition: "all 0.2s",
        }}
      >
        <RiAddLine size={16} color={nights >= maxNights ? T.textMuted : T.white} />
      </button>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 8 }}>
        {label} {required && <span style={{ color: T.primary }}>*</span>}
      </label>
      {children}
    </div>
  );
}

function FocusInput({ icon: Icon, ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      {Icon && (
        <Icon style={{
          position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)",
          color: focused ? T.primary : T.textMuted, fontSize: 16, pointerEvents: "none",
        }} />
      )}
      <input {...props} style={Icon ? iconInputStyle(focused) : inputStyle(focused)}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} />
    </div>
  );
}

/* ─── MAIN COMPONENT ─────────────────────────────────────────────────────────*/
export default function Registration() {
  const location = useLocation();
  // ✅ The URL parameter is 'id' (from SummitDetails route); we alias it as 'slug'
  const { id: slug } = useParams();
  
  // Get event data from navigation state
  const eventFromState = location.state || {};
  const { eventId: eventIdFromState, eventTitle: eventTitleFromState } = eventFromState;

  const [event, setEvent] = useState(null);
  const [loadingEvent, setLoadingEvent] = useState(true);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [pricingTiers, setPricingTiers] = useState([]);
  const [loadingPricing, setLoadingPricing] = useState(true);
  const [accommodations, setAccommodations] = useState([]);
  const [loadingAccommodations, setLoadingAccommodations] = useState(true);

  // ── Fetch event data using /api/events/{slug} (without '/slug/') ──
  useEffect(() => {
    const fetchEvent = async () => {
      if (!slug) {
        setLoadingEvent(false);
        return;
      }
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${API_BASE}/api/events/${slug}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json();
        console.log('📋 Registration - Event data from slug:', data);
        
        if (data.success && data.event) {
          setEvent(data.event);
        } else if (data.event) {
          setEvent(data.event);
        } else {
          console.warn('Event not found for slug:', slug);
        }
      } catch (error) {
        console.error('Error fetching event:', error);
      } finally {
        setLoadingEvent(false);
      }
    };
    fetchEvent();
  }, [slug]);

  // ── Fetch accommodations using event._id ──
  useEffect(() => {
    const fetchAccommodationsForEvent = async () => {
      const eventId = event?._id || eventIdFromState;

      if (!eventId) {
        setAccommodations([defaultAccommodationOption]);
        setLoadingAccommodations(false);
        return;
      }

      setLoadingAccommodations(true);
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_BASE}/api/accommodation/event/${eventId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success && response.data.data?.length > 0) {
          const formatted = [
            defaultAccommodationOption,
            ...response.data.data.map(acc => ({
              value: acc._id,
              label: acc.title,
              price: acc.price,
              description: acc.description || `${acc.title} room`,
              features: acc.features || [],
              color: acc.color || T.primaryLight,
              icon: RiHotelLine,
            }))
          ];
          setAccommodations(formatted);
        } else {
          setAccommodations([defaultAccommodationOption]);
        }
      } catch (error) {
        console.error('❌ Error fetching accommodations:', error);
        toast.error('Failed to load accommodation options');
        setAccommodations([defaultAccommodationOption]);
      } finally {
        setLoadingAccommodations(false);
      }
    };

    fetchAccommodationsForEvent();
  }, [event, eventIdFromState]);

  // ── Fetch speaker pricing tiers for this event ──
  useEffect(() => {
    const fetchPricing = async () => {
      const eventId = event?._id || eventIdFromState;

      // If no eventId, use fallback defaults
      if (!eventId) {
        console.warn('No eventId for pricing – using fallback');
        setPricingTiers([
          { title: "Student Delegate", price: 299, color: T.accent, features: ["Access to all sessions", "Networking events", "Conference materials", "Certificate of attendance"] },
          { title: "Oral Talk", price: 699, color: T.primary, features: ["All Student features", "Oral presentation slot", "Proceedings publication", "Speaker recognition"], featured: true },
          { title: "Delegate / Listener", price: 799, color: T.primaryLight, features: ["All Student features", "Premium networking", "Lunch & refreshments", "Exclusive workshop access"] },
        ]);
        if (!formData.summitType) {
          setFormData(prev => ({ ...prev, summitType: "student_delegate" }));
        }
        setLoadingPricing(false);
        return;
      }

      setLoadingPricing(true);
      try {
        const token = localStorage.getItem("token");
        // ✅ Event-specific pricing endpoint
        const res = await axios.get(`${API_BASE}/api/speaker-pricing/active/${eventId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (res.data.success && res.data.data?.length > 0) {
          setPricingTiers(res.data.data);
          // Auto-select the first tier if none selected
          if (!formData.summitType) {
            const first = res.data.data[0];
            setFormData(prev => ({
              ...prev,
              summitType: first.title.toLowerCase().replace(/\s+/g, "_")
            }));
          }
        } else {
          // No pricing for this event – fallback
          console.warn('No pricing tiers found for this event – using fallback');
          setPricingTiers([
            { title: "Student Delegate", price: 299, color: T.accent, features: ["Access to all sessions", "Networking events", "Conference materials", "Certificate of attendance"] },
            { title: "Oral Talk", price: 699, color: T.primary, features: ["All Student features", "Oral presentation slot", "Proceedings publication", "Speaker recognition"], featured: true },
            { title: "Delegate / Listener", price: 799, color: T.primaryLight, features: ["All Student features", "Premium networking", "Lunch & refreshments", "Exclusive workshop access"] },
          ]);
          if (!formData.summitType) {
            setFormData(prev => ({ ...prev, summitType: "student_delegate" }));
          }
        }
      } catch (error) {
        console.error('❌ Error fetching pricing:', error);
        toast.error('Failed to load pricing – using defaults');
        setPricingTiers([
          { title: "Student Delegate", price: 299, color: T.accent, features: ["Access to all sessions", "Networking events", "Conference materials", "Certificate of attendance"] },
          { title: "Oral Talk", price: 699, color: T.primary, features: ["All Student features", "Oral presentation slot", "Proceedings publication", "Speaker recognition"], featured: true },
          { title: "Delegate / Listener", price: 799, color: T.primaryLight, features: ["All Student features", "Premium networking", "Lunch & refreshments", "Exclusive workshop access"] },
        ]);
        if (!formData.summitType) {
          setFormData(prev => ({ ...prev, summitType: "student_delegate" }));
        }
      } finally {
        setLoadingPricing(false);
      }
    };

    fetchPricing();
  }, [event, eventIdFromState]); // Re-fetch when event or state changes

  // Use event from API first, then state as fallback
  const initialEventId = event?._id || eventIdFromState || '';
  const initialEventTitle = event?.title || eventTitleFromState || slug || '';

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    jobTitle: "",
    summitType: "",
    accommodation: "none",
    nights: 3,
    specialRequirements: "",
    agreeToTerms: false,
    eventId: initialEventId,
    eventTitle: initialEventTitle,
  });

  // Update formData when event is fetched
  useEffect(() => {
    if (event) {
      setFormData(prev => ({
        ...prev,
        eventId: event._id,
        eventTitle: event.title
      }));
    }
  }, [event]);

  // Log event data when component mounts
  useEffect(() => {
    if (initialEventId || initialEventTitle) {
      console.log(`📌 Registering for event: ${initialEventTitle || 'Unknown'} (ID: ${initialEventId || 'N/A'})`);
    }
  }, [initialEventId, initialEventTitle]);

  // Preselect from pricing page navigation
  useEffect(() => {
    if (location.state?.selectedTier) {
      const passType = location.state.selectedTier.title.toLowerCase().replace(/\s+/g, "_");
      setFormData((prev) => ({ ...prev, summitType: passType }));
    }
  }, [location.state]);

  // Helpers
  const passTypes = pricingTiers.map((t) => ({
    value: t.title.toLowerCase().replace(/\s+/g, "_"),
    label: t.title,
    price: t.price,
    priceDisplay: t.priceDisplay || `$${t.price}`,
    color: t.color || T.primary,
    features: t.features || [],
  }));

  const selectedPass = passTypes.find((p) => p.value === formData.summitType);
  const selectedAccom = accommodations.find((a) => a.value === formData.accommodation);
  const passPrice = selectedPass?.price || 0;
  const accomPrice = (selectedAccom?.price || 0) * formData.nights;
  const subtotal = passPrice + (formData.accommodation !== "none" ? accomPrice : 0);
  const processingFee = subtotal * 0.05;
  const totalAmount = subtotal + processingFee;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const validateStep1 = () => {
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
      toast.error("Please fill all required fields");
      return false;
    }
    if (!formData.email.includes("@")) {
      toast.error("Please enter a valid email");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step === 1) {
      if (!validateStep1()) return;
      setStep(2);
      return;
    }
    if (step === 2) {
      if (!formData.summitType) {
        toast.error("Please select a pass");
        return;
      }
      setStep(3);
      return;
    }
    if (step === 3) {
      setStep(4);
      return;
    }
    if (step === 4) {
      if (!formData.agreeToTerms) {
        toast.error("Please agree to Terms & Conditions");
        return;
      }
      setLoading(true);
      try {
        const registrationData = {
          eventId: formData.eventId,
          eventName: formData.eventTitle || 'Event',
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          jobTitle: formData.jobTitle,
          summitType: formData.summitType,
          summitTitle: selectedPass?.label,
          summitPrice: selectedPass?.price,
          accommodation: formData.accommodation,
          accommodationTitle: selectedAccom?.label,
          accommodationPrice: selectedAccom?.price || 0,
          nights: formData.nights,
          specialRequirements: formData.specialRequirements || "",
          subtotalAmount: subtotal,
          processingFee,
          totalAmount,
        };

        console.log('📝 Submitting registration with eventName:', registrationData.eventName);

        const res = await axios.post(`${API_BASE}/api/registrations`, registrationData);
        
        if (res.data.success) {
          toast.success(`Registration for "${formData.eventTitle || 'Event'}" successful! 🎉`);
          setStep(5);
        } else throw new Error(res.data.message || "Registration failed");
      } catch (err) {
        console.error('Registration error:', err);
        toast.error(err.response?.data?.message || "Registration failed. Please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  if (loadingPricing || loadingAccommodations || loadingEvent) {
    return (
      <div style={{ minHeight: "100vh", background: T.offWhite, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <FontImport />
        <div style={{
          width: 44, height: 44, border: `3px solid ${T.border}`, borderTopColor: T.primary,
          borderRadius: "50%", animation: "spin 1s linear infinite",
        }} />
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: T.offWhite, fontFamily: "'Inter', sans-serif", color: T.text }}>
      <FontImport />
      <Toaster position="top-right" toastOptions={{
        style: { background: T.white, color: T.text, border: `1px solid ${T.border}`, borderRadius: 10, fontSize: 13 },
      }} />

      {/* ── HERO SECTION ── */}
      <section style={{ padding: "clamp(48px,7vw,80px) clamp(20px,4vw,64px) clamp(40px,5vw,64px)", background: T.gradSoft, borderBottom: `1px solid ${T.border}`, position: "relative", overflow: "hidden" }}>
        <div style={{ maxWidth: 1320, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
            <span style={{ 
              padding: "5px 14px", 
              borderRadius: 20, 
              background: T.softTeal, 
              border: `1px solid ${T.border}`, 
              color: T.primary, 
              fontSize: 11, 
              fontWeight: 700, 
              textTransform: "uppercase", 
              letterSpacing: "0.1em" 
            }}>
              <RiUserAddLine size={13} style={{ display: "inline", marginRight: 6 }} />
              {formData.eventTitle ? `Register for: ${formData.eventTitle}` : 'Conference Registration'}
            </span>
          </div>
          <h1 style={{ 
            fontFamily: "'Playfair Display', serif", 
            fontSize: "clamp(2rem, 4.5vw, 3.4rem)", 
            fontWeight: 900, 
            color: T.navy, 
            letterSpacing: "-0.03em", 
            lineHeight: 1.06, 
            marginBottom: 14 
          }}>
            Online <em style={{ color: T.accentGold, fontStyle: "normal" }}>Registration Form</em>
          </h1>
          <p style={{ 
            fontSize: "clamp(0.88rem, 1.3vw, 1rem)", 
            color: T.textMid, 
            lineHeight: 1.7, 
            maxWidth: 560 
          }}>
            Join industry leaders, innovators, and visionaries at the most anticipated event of the year.
          </p>
          
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 24 }}>
            <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
            <div style={{ width: 8, height: 8, background: T.accentGold, transform: "rotate(45deg)" }} />
            <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
          </div>
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <div style={{ maxWidth: 1320, margin: "0 auto", padding: "24px 40px 0" }}>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 24 }}>
          <div style={{ background: T.white, borderRadius: 12, padding: "16px 24px", border: `1px solid ${T.border}`, flex: "1 1 auto", minWidth: 120 }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: T.accentGold }}>{passTypes.length}</div>
            <div style={{ fontSize: 12, color: T.textMuted }}>Pass Types</div>
          </div>
          <div style={{ background: T.white, borderRadius: 12, padding: "16px 24px", border: `1px solid ${T.border}`, flex: "1 1 auto", minWidth: 120 }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: T.primary }}>{accommodations.length - 1}</div>
            <div style={{ fontSize: 12, color: T.textMuted }}>Hotels</div>
          </div>
          <div style={{ background: T.white, borderRadius: 12, padding: "16px 24px", border: `1px solid ${T.border}`, flex: "1 1 auto", minWidth: 120 }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: T.accentTealLight }}>3</div>
            <div style={{ fontSize: 12, color: T.textMuted }}>Days</div>
          </div>
        </div>
      </div>

      {/* ── CONTENT ── */}
      <div style={{ maxWidth: 1000, margin: "0 auto", padding: "0 40px 80px" }}>
        <StepIndicator currentStep={step} />

        <div style={{
          background: T.white, borderRadius: 28, border: `1px solid ${T.border}`,
          padding: "40px 48px", boxShadow: T.shadowLg,
        }}>
          {/* Event Name Banner */}
          {formData.eventTitle && (
            <div style={{
              background: T.softTeal,
              borderRadius: 12,
              padding: '16px 20px',
              marginBottom: 24,
              border: `1px solid ${T.border}`,
              display: 'flex',
              alignItems: 'center',
              gap: 12
            }}>
              <RiCalendarEventLine size={20} color={T.primary} />
              <div>
                <div style={{ fontSize: 12, color: T.textMuted, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                  Registering for
                </div>
                <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>
                  {formData.eventTitle}
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <AnimatePresence mode="wait">
              {/* ── STEP 1: PERSONAL INFO ── */}
              {step === 1 && (
                <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.35 }}>
                  <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 800, color: T.text, marginBottom: 6 }}>
                    Personal Information
                  </h2>
                  <p style={{ color: T.textMuted, marginBottom: 32, fontSize: 14 }}>
                    Please provide your contact details for registration
                  </p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                      <Field label="First Name" required>
                        <FocusInput type="text" name="firstName" value={formData.firstName} onChange={handleChange} placeholder="John" />
                      </Field>
                      <Field label="Last Name" required>
                        <FocusInput type="text" name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Doe" />
                      </Field>
                    </div>
                    <Field label="Email Address" required>
                      <FocusInput icon={RiMailLine} type="email" name="email" value={formData.email} onChange={handleChange} placeholder="john@example.com" />
                    </Field>
                    <Field label="Phone Number" required>
                      <FocusInput icon={RiPhoneLine} type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="+1 (234) 567-8900" />
                    </Field>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                      <Field label="Company">
                        <FocusInput icon={RiBuildingLine} type="text" name="company" value={formData.company} onChange={handleChange} placeholder="Company Inc." />
                      </Field>
                      <Field label="Job Title">
                        <FocusInput icon={RiBriefcaseLine} type="text" name="jobTitle" value={formData.jobTitle} onChange={handleChange} placeholder="Software Engineer" />
                      </Field>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* ── STEP 2: SELECT PASS ── */}
              {step === 2 && (
                <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.35 }}>
                  <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 800, color: T.text, marginBottom: 6 }}>
                    Select Your Pass
                  </h2>
                  <p style={{ color: T.textMuted, marginBottom: 32, fontSize: 14 }}>
                    Choose the pass that best fits your needs
                  </p>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 20 }}>
                    {passTypes.map((pass, i) => (
                      <PassCard key={pass.value} pass={pass} index={i} isSelected={formData.summitType === pass.value}
                        onSelect={(val) => setFormData((prev) => ({ ...prev, summitType: val }))} />
                    ))}
                  </div>
                </motion.div>
              )}

              {/* ── STEP 3: ACCOMMODATION ── */}
              {step === 3 && (
                <motion.div key="s3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.35 }}>
                  <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 800, color: T.text, marginBottom: 6 }}>
                    Accommodation
                  </h2>
                  <p style={{ color: T.textMuted, marginBottom: 32, fontSize: 14 }}>
                    Select your accommodation preference (hotel near venue)
                  </p>
                  
                  {/* Debug: Show number of accommodations */}
                  <div style={{ 
                    fontSize: 14, 
                    color: T.primary, 
                    marginBottom: 16,
                    padding: "8px 16px",
                    background: T.softTeal,
                    borderRadius: 8,
                    display: "inline-block"
                  }}>
                    {accommodations.length - 1} accommodations available
                  </div>
                  
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 16 }}>
                    {accommodations.map((acc, i) => (
                      <AccommodationCard key={acc.value} accommodation={acc} index={i}
                        isSelected={formData.accommodation === acc.value}
                        onSelect={(val) => setFormData((prev) => ({ ...prev, accommodation: val }))} />
                    ))}
                  </div>
                  {formData.accommodation !== "none" && (
                    <div style={{ marginTop: 28, padding: "20px 24px", background: T.offWhite, borderRadius: 16, border: `1px solid ${T.border}` }}>
                      <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 14 }}>Number of Nights</label>
                      <NightCounter nights={formData.nights} onNightsChange={(n) => setFormData((prev) => ({ ...prev, nights: n }))} />
                    </div>
                  )}
                  <div style={{ marginTop: 24 }}>
                    <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 8 }}>
                      Special Requirements <span style={{ color: T.textMuted }}>(Optional)</span>
                    </label>
                    <textarea name="specialRequirements" value={formData.specialRequirements} onChange={handleChange} rows="3"
                      placeholder="Dietary requirements, accessibility needs, or other requests…"
                      style={{ width: "100%", padding: "12px 16px", background: T.white, border: `1px solid ${T.border}`,
                        borderRadius: 12, fontSize: 14, color: T.text, fontFamily: "'Inter', sans-serif", resize: "vertical",
                        outline: "none", transition: "border-color 0.2s" }}
                      onFocus={(e) => (e.currentTarget.style.borderColor = T.primary)}
                      onBlur={(e) => (e.currentTarget.style.borderColor = T.border)} />
                  </div>
                </motion.div>
              )}

              {/* ── STEP 4: CONFIRM ── */}
              {step === 4 && selectedPass && (
                <motion.div key="s4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.35 }}>
                  <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 800, color: T.text, marginBottom: 6 }}>
                    Confirm Registration
                  </h2>
                  <p style={{ color: T.textMuted, marginBottom: 32, fontSize: 14 }}>
                    Review your details before finalising
                  </p>

                  <div style={{ background: T.offWhite, borderRadius: 16, padding: 24, marginBottom: 20, border: `1px solid ${T.border}` }}>
                    <SectionLabel label="Personal Details" />
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                      {[["Full Name", `${formData.firstName} ${formData.lastName}`], ["Email", formData.email], ["Phone", formData.phone],
                        formData.company && ["Company", formData.company]]
                        .filter(Boolean).map(([lbl, val]) => (
                          <div key={lbl}>
                            <span style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.07em" }}>{lbl}</span>
                            <p style={{ fontSize: 14, fontWeight: 600, color: T.text, marginTop: 4 }}>{val}</p>
                          </div>
                        ))}
                    </div>
                  </div>

                  {formData.eventTitle && (
                    <div style={{ background: T.softTeal, borderRadius: 12, padding: 16, marginBottom: 20, border: `1px solid ${T.border}` }}>
                      <div style={{ fontSize: 12, color: T.textMuted, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                        Event
                      </div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>
                        {formData.eventTitle}
                      </div>
                    </div>
                  )}

                  <div style={{
                    background: `${selectedPass.color}08`, borderRadius: 20, padding: 28, marginBottom: 20,
                    border: `1px solid ${selectedPass.color}20`,
                  }}>
                    <SectionLabel label="Order Summary" />
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                      <div>
                        <div style={{ fontSize: 11, color: selectedPass.color, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 3 }}>Pass</div>
                        <div style={{ fontSize: 17, fontWeight: 800, fontFamily: "'Playfair Display', serif", color: T.text }}>{selectedPass.label}</div>
                      </div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: selectedPass.color }}>{selectedPass.priceDisplay}</div>
                    </div>
                    {selectedAccom && selectedAccom.value !== "none" && (
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 14, borderTop: `1px solid ${T.border}`, marginBottom: 14 }}>
                        <div>
                          <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 3 }}>Accommodation</div>
                          <div style={{ fontSize: 15, fontWeight: 600, color: T.text }}>{selectedAccom.label} — {formData.nights} {formData.nights === 1 ? "Night" : "Nights"}</div>
                        </div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: T.accent }}>${(selectedAccom.price * formData.nights).toFixed(2)}</div>
                      </div>
                    )}
                    <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 14 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                        <span style={{ fontSize: 13, color: T.textMuted }}>Subtotal</span>
                        <span style={{ fontSize: 15, fontWeight: 600, color: T.text }}>${subtotal.toFixed(2)}</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                        <span style={{ fontSize: 13, color: T.textMuted }}>Processing Fee (5%)</span>
                        <span style={{ fontSize: 15, fontWeight: 600, color: T.textMuted }}>${processingFee.toFixed(2)}</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 14, borderTop: `2px solid ${T.border}`, marginTop: 8 }}>
                        <span style={{ fontSize: 16, fontWeight: 700, color: T.text }}>Total</span>
                        <span style={{ fontSize: 28, fontWeight: 900, fontFamily: "'Playfair Display', serif", color: T.accentGold }}>${totalAmount.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  {formData.specialRequirements && (
                    <div style={{ background: T.offWhite, borderRadius: 14, padding: 18, marginBottom: 20, border: `1px solid ${T.border}` }}>
                      <span style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.07em" }}>Special Requirements</span>
                      <p style={{ fontSize: 14, color: T.text, marginTop: 6, lineHeight: 1.6 }}>{formData.specialRequirements}</p>
                    </div>
                  )}

                  <label style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}>
                    <input type="checkbox" name="agreeToTerms" checked={formData.agreeToTerms} onChange={handleChange}
                      style={{ width: 18, height: 18, cursor: "pointer", accentColor: T.primary }} />
                    <span style={{ fontSize: 14, color: T.textMuted }}>
                      I agree to the <Link to="/terms" style={{ color: T.primary, textDecoration: "none" }}>Terms & Conditions</Link>{" "}
                      and <Link to="/privacy-policy" style={{ color: T.primary, textDecoration: "none" }}>Privacy Policy</Link>
                    </span>
                  </label>
                </motion.div>
              )}

              {/* ── STEP 5: SUCCESS ── */}
              {step === 5 && (
                <motion.div key="s5" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }} style={{ textAlign: "center", padding: "40px 20px" }}>
                  <div style={{
                    width: 80, height: 80, borderRadius: "50%", background: `${T.success}10`,
                    border: `1px solid ${T.success}30`, display: "flex", alignItems: "center",
                    justifyContent: "center", margin: "0 auto 24px",
                  }}>
                    <RiCheckLine size={44} color={T.success} />
                  </div>
                  <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 30, fontWeight: 800, color: T.text, marginBottom: 10 }}>
                    Registration Successful! 🎉
                  </h2>
                  <p style={{ color: T.textMuted, marginBottom: 6 }}>Welcome, {formData.firstName} {formData.lastName}!</p>
                  {formData.eventTitle && (
                    <p style={{ color: T.primary, marginBottom: 6, fontWeight: 600 }}>
                      You are registered for: {formData.eventTitle}
                    </p>
                  )}
                  <p style={{ color: T.textMuted, marginBottom: 32 }}>
                    A confirmation email has been sent to <span style={{ color: T.primary, fontWeight: 600 }}>{formData.email}</span>
                  </p>
                  <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <Link to="/" style={{
                      display: "inline-flex", alignItems: "center", gap: 8, background: T.gradGold,
                      color: T.navyDeep, padding: "13px 32px", borderRadius: 50, fontSize: 14,
                      fontWeight: 700, textDecoration: "none", transition: "all 0.2s", boxShadow: T.shadowGold,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = T.shadowLg; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = T.shadowGold; }}>
                      Back to Home <RiArrowRightLine size={16} />
                    </Link>
                    {formData.eventId && (
                      <Link to={`/event/${slug || formData.eventId}`} style={{
                        display: "inline-flex", alignItems: "center", gap: 8,
                        background: "transparent", border: `2px solid ${T.primary}`,
                        color: T.primary, padding: "13px 32px", borderRadius: 50, fontSize: 14,
                        fontWeight: 700, textDecoration: "none", transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = T.primary; e.currentTarget.style.color = T.white; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = T.primary; }}>
                        View Event <RiCalendarEventLine size={16} />
                      </Link>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* ── NAV BUTTONS ── */}
            {step < 5 && (
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 40, paddingTop: 24, borderTop: `1px solid ${T.border}` }}>
                {step > 1 ? (
                  <button type="button" onClick={() => setStep((p) => p - 1)}
                    style={{ padding: "12px 28px", background: "transparent", border: `1px solid ${T.border}`,
                      borderRadius: 40, fontSize: 14, fontWeight: 600, color: T.textMuted, cursor: "pointer",
                      transition: "all 0.2s" }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.primary; e.currentTarget.style.color = T.primary; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textMuted; }}>
                    Previous
                  </button>
                ) : <div />}
                <button type="submit" disabled={loading}
                  style={{ padding: "12px 36px", background: T.gradGold, border: "none", borderRadius: 40,
                    fontSize: 14, fontWeight: 700, color: T.navyDeep, cursor: loading ? "not-allowed" : "pointer",
                    opacity: loading ? 0.7 : 1, transition: "all 0.2s", display: "flex", alignItems: "center", gap: 8,
                    boxShadow: T.shadowGold }}
                  onMouseEnter={(e) => { if (!loading) { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = T.shadowLg; } }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = T.shadowGold; }}>
                  {loading ? <> <div style={{ width: 16, height: 16, border: "2px solid #1A3A4A", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} /> Processing…</> :
                  <>{step === 4 ? "Complete Registration" : "Continue"} <RiArrowRightLine size={16} /></>}
                </button>
              </div>
            )}
          </form>
        </div>

        {/* Explore More About Summits Button */}
        <div style={{ display: "flex", justifyContent: "center", marginTop: 48 }}>
          <Link to="/events" style={{
            display: "inline-flex", alignItems: "center", gap: 12,
            background: "transparent", border: `2px solid ${T.primary}`,
            color: T.primary, padding: "14px 32px", borderRadius: 50,
            fontSize: 15, fontWeight: 700, textDecoration: "none",
            transition: "all 0.3s", cursor: "pointer",
          }}
          onMouseEnter={(e) => { 
            e.currentTarget.style.background = T.gradGold;
            e.currentTarget.style.color = T.navyDeep;
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = T.shadowGold;
            e.currentTarget.style.borderColor = "transparent";
          }}
          onMouseLeave={(e) => { 
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = T.primary;
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "none";
            e.currentTarget.style.borderColor = T.primary;
          }}>
            <RiCalendarEventLine size={20} />
            Explore More About Summits
            <RiArrowRightLine size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
}